/**
 * Los cuatro ajustes editables de Dispositivos: modelos, tipos de incidencia,
 * mínimos de stock y kit de casa nueva. Viven en Configuración
 * (`settings/dispositivos_*`) y se piden una vez por sesión, como los puestos
 * y las zonas de la bolsa de empleo.
 */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { IncidenciaTipoAjuste, KitAjuste, MinimoAjuste, ModeloAjuste, TipoDispositivo } from '@/lib/dispositivos';

export interface CatalogosDispositivos {
  modelos: ModeloAjuste[];
  incidenciaTipos: IncidenciaTipoAjuste[];
  minimos: MinimoAjuste[];
  kit: KitAjuste[];
  modeloLabel: Record<string, string>;
  incidenciaLabel: Record<string, string>;
}

const VACIO: CatalogosDispositivos = {
  modelos: [],
  incidenciaTipos: [],
  minimos: [],
  kit: [],
  modeloLabel: {},
  incidenciaLabel: {},
};

let cache: CatalogosDispositivos | null = null;
let enVuelo: Promise<CatalogosDispositivos> | null = null;

async function cargar(): Promise<CatalogosDispositivos> {
  const ajustes = await api.getSettings();
  const valores = <T,>(clave: string): T[] =>
    (ajustes.find((a: { key?: string; id?: string }) => a.key === clave || a.id === clave)?.values || []) as T[];
  const modelos = valores<ModeloAjuste>('dispositivos_modelos');
  const incidenciaTipos = valores<IncidenciaTipoAjuste>('dispositivos_incidencia_tipos');
  return {
    modelos,
    incidenciaTipos,
    minimos: valores<MinimoAjuste>('dispositivos_minimos'),
    kit: valores<KitAjuste>('dispositivos_kit'),
    modeloLabel: Object.fromEntries(modelos.map((m) => [m.id, m.label])),
    incidenciaLabel: Object.fromEntries(incidenciaTipos.map((t) => [t.id, t.label])),
  };
}

/** Lo llama Configuración al guardar cualquiera de los cuatro. */
export function olvidarCatalogosDispositivos(): void {
  cache = null;
  enVuelo = null;
}

export function useCatalogosDispositivos(): { catalogos: CatalogosDispositivos; cargando: boolean } {
  const [catalogos, setCatalogos] = useState<CatalogosDispositivos>(cache || VACIO);
  const [cargando, setCargando] = useState(!cache);

  useEffect(() => {
    if (cache) return;
    let vigente = true;
    enVuelo = enVuelo || cargar();
    enVuelo
      .then((c) => {
        cache = c;
        if (vigente) setCatalogos(c);
      })
      .catch(() => {
        enVuelo = null;
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, []);

  return { catalogos, cargando };
}

/** Los modelos que caben en un tipo, para el desplegable. */
export function modelosDe(catalogos: CatalogosDispositivos, tipo: TipoDispositivo): ModeloAjuste[] {
  return catalogos.modelos.filter((m) => m.tipo === tipo);
}
